import { Capacitor } from '@capacitor/core';
import { Geolocation, type PermissionState } from '@capacitor/geolocation';

export type AppGeoPosition = {
  lat: number;
  lng: number;
  accuracy?: number;
};

export type GeoPermissionState = 'granted' | 'denied' | 'prompt';

export class GeolocationError extends Error {
  code: number;

  constructor(code: number, message: string) {
    super(message);
    this.name = 'GeolocationError';
    this.code = code;
  }
}

const mapCapacitorPermission = (state: PermissionState): GeoPermissionState => {
  if (state === 'granted') return 'granted';
  if (state === 'denied') return 'denied';
  return 'prompt';
};

export const isNativeApp = (): boolean => Capacitor.isNativePlatform();

export { getChromeSiteLocationSettingsUrl } from './geoDefaults';

export const getLocationPermissionDeniedHint = (): string => {
  if (isNativeApp()) {
    return '請到手機「設定 → 應用程式 → 快修通 → 權限 → 位置」設為允許，然後重開 App。或在地圖上點擊設置您的位置。';
  }
  return '請點擊地址欄左側的鎖圖標 → 網站設置 → 將「位置」改為「允許」，然後重新整理。或在地圖上點擊設置您的位置。';
};

export function getGeolocationErrorMessage(error: unknown): string {
  if (!(error instanceof GeolocationError)) {
    return '獲取位置失敗';
  }
  if (error.code === 1) {
    return getLocationPermissionDeniedHint();
  }
  if (error.code === 2) {
    return '無法獲取位置信息，請檢查您的設備 GPS 或網路連線';
  }
  if (error.code === 3) {
    return '獲取位置超時，請重試';
  }
  return error.message || '獲取位置失敗';
}

export async function checkLocationPermission(): Promise<GeoPermissionState> {
  if (isNativeApp()) {
    const status = await Geolocation.checkPermissions();
    return mapCapacitorPermission(status.location);
  }

  if (navigator.permissions?.query) {
    try {
      const status = await navigator.permissions.query({ name: 'geolocation' });
      return status.state as GeoPermissionState;
    } catch {
      return 'prompt';
    }
  }

  return 'prompt';
}

export async function requestLocationPermission(): Promise<GeoPermissionState> {
  if (isNativeApp()) {
    const current = await Geolocation.checkPermissions();
    if (current.location === 'granted') return 'granted';

    const requested = await Geolocation.requestPermissions();
    return mapCapacitorPermission(requested.location);
  }

  return checkLocationPermission();
}

type GetCurrentPositionOptions = {
  enableHighAccuracy?: boolean;
  timeout?: number;
  maximumAge?: number;
};

export async function getAppCurrentPosition(
  options: GetCurrentPositionOptions = {}
): Promise<AppGeoPosition> {
  const {
    enableHighAccuracy = false,
    timeout = 10000,
    maximumAge = 0,
  } = options;

  if (isNativeApp()) {
    const permission = await requestLocationPermission();
    if (permission !== 'granted') {
      throw new GeolocationError(1, 'PERMISSION_DENIED');
    }

    const position = await Geolocation.getCurrentPosition({
      enableHighAccuracy,
      timeout,
      maximumAge,
    });

    return {
      lat: position.coords.latitude,
      lng: position.coords.longitude,
      accuracy: position.coords.accuracy,
    };
  }

  if (!navigator.geolocation) {
    throw new GeolocationError(2, 'POSITION_UNAVAILABLE');
  }

  return new Promise((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
          accuracy: position.coords.accuracy,
        });
      },
      (error) => {
        reject(new GeolocationError(error.code, error.message));
      },
      {
        enableHighAccuracy,
        timeout,
        maximumAge,
      }
    );
  });
}
